import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { createMap, activityIcon } from '../../../utils/leafletConfig';
import * as primarycolor from '../../primarycolor';

const PolylineMap_Activities = ({ initialCoordinates }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);

  useEffect(() => {
    // Initialize map
    if (mapRef.current && !mapInstanceRef.current) {
      mapInstanceRef.current = createMap(mapRef.current);
    }
    
    return () => {
      // Cleanup on unmount
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !initialCoordinates) return;

    // Clear existing markers and polylines
    markersRef.current.forEach(marker => {
      mapInstanceRef.current.removeLayer(marker);
    });
    markersRef.current = [];

    if (polylineRef.current) {
      mapInstanceRef.current.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }

    // Add new markers and polyline
    if (initialCoordinates.length > 0) {
      const polylinePositions = [];

      initialCoordinates.forEach((marker, index) => {
        const lat = Number(marker.lat);
        const lng = Number(marker.lng);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          // Add marker
          const leafletMarker = L.marker([lat, lng], { icon: activityIcon })
            .bindPopup(`
              <div style="font-family: 'Segoe UI', sans-serif; max-width: 280px;">
                <div style="background: linear-gradient(135deg, #52c41a, #73d13d); color: white; padding: 8px 12px; margin: -8px -12px 8px -12px; border-radius: 4px 4px 0 0;">
                  <strong style="font-size: 14px;">🚚 ${marker.activity_name || 'Activity'}</strong>
                </div>
                <div style="padding: 4px 0;">
                  <div style="margin-bottom: 8px;">
                    <div style="display: flex; align-items: center; margin-bottom: 4px;">
                      <span style="background: #52c41a; color: white; border-radius: 12px; padding: 2px 8px; font-size: 10px; font-weight: bold;">
                        #${index + 1}
                      </span>
                      <span style="margin-left: 8px; font-size: 11px; color: #666;">
                        Activity Point
                      </span>
                    </div>
                  </div>
                  <div style="background: #f5f5f5; padding: 6px; border-radius: 4px; font-size: 11px; color: #666; margin-bottom: 6px;">
                    <strong>📍 Coordinates:</strong> ${marker.lat}, ${marker.lng}
                  </div>
                  ${marker.timestamp ? `
                    <div style="font-size: 11px; color: #666;">
                      🕒 <strong>Time:</strong> ${marker.timestamp}
                    </div>
                  ` : ''}
                </div>
              </div>
            `, {
              maxWidth: 300,
              className: 'professional-popup'
            })
            .addTo(mapInstanceRef.current);
          
          markersRef.current.push(leafletMarker);
          polylinePositions.push([lat, lng]);
        }
      });

      // Add polyline if there are multiple points
      if (polylinePositions.length > 1) {
        polylineRef.current = L.polyline(polylinePositions, {
          color: primarycolor.primarycolor || '#00008B',
          weight: 4,
          opacity: 0.8,
          dashArray: '5, 10',
          lineCap: 'round',
          lineJoin: 'round'
        }).addTo(mapInstanceRef.current);

        // Add arrows to show direction
        const arrowIcon = L.divIcon({
          html: '➤',
          className: 'arrow-icon',
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        });

        // Add direction arrows at midpoints
        for (let i = 0; i < polylinePositions.length - 1; i++) {
          const start = polylinePositions[i];
          const end = polylinePositions[i + 1];
          const midpoint = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
          
          L.marker(midpoint, { icon: arrowIcon }).addTo(mapInstanceRef.current);
        }
      }

      // Fit map to show all markers
      if (markersRef.current.length > 0) {
        const group = new L.featureGroup(markersRef.current);
        mapInstanceRef.current.fitBounds(group.getBounds(), { 
          padding: [30, 30],
          maxZoom: 14
        });
      }
    }
  }, [initialCoordinates]);

  return (
    <div 
      ref={mapRef}
      style={{
        width: '100%',
        height: '500px',
        border: '1px solid #e8e8e8',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}
    />
  );
};

export default PolylineMap_Activities;